import { Image, Tooltip, Popover } from "antd";
import { Link, navigate } from "gatsby";
import React, { useContext, useState } from "react";
import {
  LuSettings,
  LuChartPie,
  LuUsers,
  LuMessagesSquare,
  LuMessageCircle,
  LuStar,
  LuFileText,
  LuCar,
  LuMegaphone,
  LuCircleHelp,
  LuChevronLeft,
  LuChevronRight,
  LuChevronDown,
} from "react-icons/lu";
import { UserContext } from "../../amplify/authenticator-provider";
import { getEffectiveCognitoRoles } from "../../hooks/utils";
import { openDialog } from "../../library/helpers";
import { getRoutePath, ROUTES } from "../../Routes";
// Horizontal lockup for the expanded rail — vector, so it stays sharp at the
// small render size where the PNG export softened.
import Logo from "../layoutComponent/logoWithText.svg";

// Square graphic-only mark for the collapsed rail, where the horizontal
// wordmark has no legible size. Lives in static/, so it's a URL not an import.
const LogoMark = "/images/logo-graphic-only-lg.png";

// Shared with mainLayout so the rail and the content column can't drift.
export const SIDEBAR_BG = "#001529";
export const SIDEBAR_ACTIVE_BG = "#1677FF";
export const SIDEBAR_EXPANDED_WIDTH = 200;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

const NavigationContainer = props => {
  const {
    setContentStyle,
    accessPayload,
    collapsed = false,
    setCollapsed,
    // "drawer" is the mobile off-canvas variant: always full width, no collapse
    // toggle, and the Reports submenu inlines instead of flying out.
    variant = "sidebar",
    onNavigate,
  } = props;

  const { user } = useContext(UserContext);

  const activeSlug = typeof window !== "undefined" ? window.location.pathname : "";
  const isDrawer = variant === "drawer";
  const showLabels = isDrawer || !collapsed;

  const isAdminUser = getEffectiveCognitoRoles(accessPayload)[0] === "ADMIN";

  const NAVIGATION_OPTIONS = [
    {
      id: 1,
      label: "Admin",
      link: ROUTES.adminDashboard,
      icon: LuSettings,
      type: "setting",
      isAdminSetting: isAdminUser,
    },
    {
      id: 2,
      label: "Analytics",
      link: ROUTES.analyticsKpis,
      icon: LuChartPie,
      type: "pieChart",
      isAdminSetting: !isAdminUser && user?.role?.canViewAutoAnalytics,
    },
    {
      id: 3,
      label: "Prospects",
      link: user?.role?.canViewProspects ? ROUTES.prospect : ROUTES.noPermission,
      icon: LuUsers,
      type: "funnelPlot",
      isAdminSetting: !isAdminUser && user?.role?.canViewProspects,
    },
    {
      id: 4,
      label: "Engagements",
      link: ROUTES.engagementsDashboard,
      urlPart: "engagements",
      icon: LuMessagesSquare,
      type: "mail",
      isAdminSetting: !isAdminUser && user?.role?.canViewEngagements,
    },
    {
      id: 5,
      label: "Conversations",
      link: ROUTES.conversation,
      icon: LuMessageCircle,
      type: "message",
      isAdminSetting: !isAdminUser && (user?.role?.canViewClm || user?.role?.canViewProspects),
    },
    {
      id: 6,
      label: "Reviews",
      link: ROUTES.reviews,
      icon: LuStar,
      type: "star",
      isAdminSetting: !isAdminUser && user?.role?.canViewEngagements,
    },
    {
      id: 8,
      label: "Reports",
      link: ROUTES.reports,
      icon: LuFileText,
      type: "reports",
      isAdminSetting: !isAdminUser && user?.role?.canViewEngagements,
    },
    {
      id: 9,
      label: "Inventory",
      link: ROUTES.inventory,
      icon: LuCar,
      type: "inventory",
      isAdminSetting: !isAdminUser && user?.role?.canViewEngagements,
    },
    {
      id: 7,
      label: "Personalized",
      link: ROUTES.personalizedAds,
      icon: LuMegaphone,
      type: "download",
      isAdminSetting: !isAdminUser && user?.role?.canViewAdExport,
    },
  ];

  const [reportsOpen, setReportsOpen] = useState(false);

  const handleNavigationWidth = () => {
    setCollapsed(prev => !prev);
    setContentStyle(prev => !prev);
  };

  const userCurrentLocation =
    typeof sessionStorage !== "undefined" ? sessionStorage.getItem("currentLocationId") || "" : "";
  const locationIds = userCurrentLocation?.split(",").filter(id => id.trim());

  const go = link => {
    navigate(link);
    if (onNavigate) onNavigate();
  };

  const REPORT_LINKS = [
    {
      key: "email-analytics-reports",
      label: "Email Reports",
      to:
        locationIds?.length === 1
          ? getRoutePath(ROUTES.emailLocationAnalytics, locationIds[0])
          : ROUTES.emailAnalytics,
    },
    {
      key: "call-reports",
      label: "Call Reports",
      to:
        locationIds?.length === 1
          ? getRoutePath(ROUTES.locationReports, locationIds[0])
          : ROUTES.reports,
    },
    { key: "ottovin-reports", label: "OttoVin", to: ROUTES.ottoVinReports },
    { key: "otto-analytics", label: "Ottolytics", to: ROUTES.ottoAnalytics },
    { key: "daily-checkout-report", label: "Checkout Report", to: ROUTES.dailyCheckoutReport },
    { key: "media-report", label: "Media Report", to: ROUTES.mediaReport },
    { key: "scheduled-reports", label: "Scheduled Reports", to: ROUTES.scheduledReports },
  ];

  // Flyout for the icon-only rail, where an inline accordion has no room for
  // its labels. Plain rows rather than an antd Menu, whose 40px item height and
  // inline padding made the panel far too large for five links.
  const reportsPopoverContent = (
    <div className="flex min-w-[160px] flex-col py-1">
      {REPORT_LINKS.map(r => (
        <div
          key={r.key}
          onClick={() => go(r.to)}
          className={`cursor-pointer whitespace-nowrap px-3 py-1.5 text-sm transition-colors duration-150 ${
            activeSlug.includes(r.to)
              ? "bg-blue-50 text-blue-600"
              : "text-slate-700 hover:bg-slate-100"
          }`}
        >
          {r.label}
        </div>
      ))}
    </div>
  );

  const isLinkActive = link => {
    if (link.label === "Reports") {
      // Reports owns several unrelated route prefixes, but never anything
      // under /engagements (e.g. review-detail-report is an Engagements
      // dashboard drill-in whose slug happens to contain "report") — that
      // whole prefix belongs to the Engagements nav item instead.
      if (activeSlug.startsWith("/engagements")) return false;
      return /email-analytics|report|otto-analytics|abandoned-calls/i.test(activeSlug);
    }
    return activeSlug.includes(
      link.link === ROUTES.engagementsDashboard ? link.urlPart : link.link
    );
  };

  // Rows are inset from the rail edges so the active pill's rounded corners
  // read on all four sides.
  const itemClass = active =>
    `flex items-center gap-3 w-full cursor-pointer no-underline rounded-md transition-colors duration-150 ${
      showLabels ? "px-3" : "justify-center px-0"
    } py-2.5 ${active ? "text-white" : "text-white/65 hover:text-white hover:bg-white/10"}`;

  const itemStyle = active => (active ? { backgroundColor: SIDEBAR_ACTIVE_BG } : undefined);

  const renderItem = link => {
    if (!link.isAdminSetting) return null;

    const active = isLinkActive(link);
    const IconComponent = link.icon;
    const body = (
      <>
        <IconComponent size={18} className="shrink-0" />
        {showLabels && <span className="truncate text-sm">{link.label}</span>}
      </>
    );

    if (link.label === "Reports") {
      if (!showLabels) {
        return (
          <Popover
            key={link.id}
            content={reportsPopoverContent}
            trigger={["hover", "click"]}
            // rightTop, not right: "right" centres the panel on the icon, which
            // pushes a five-item list well above the trigger.
            placement="rightTop"
            arrow={false}
            styles={{ body: { padding: 0 } }}
          >
            <div className={itemClass(active)} style={itemStyle(active)}>
              {body}
            </div>
          </Popover>
        );
      }
      return (
        <div key={link.id}>
          <div
            className={itemClass(active)}
            style={itemStyle(active)}
            onClick={() => setReportsOpen(prev => !prev)}
          >
            {body}
            <LuChevronDown
              size={16}
              className={`ml-auto shrink-0 transition-transform duration-150 ${
                reportsOpen ? "rotate-180" : ""
              }`}
            />
          </div>
          {reportsOpen && (
            <div className="flex flex-col">
              {REPORT_LINKS.map(r => (
                <div
                  key={r.key}
                  onClick={() => go(r.to)}
                  className={`cursor-pointer rounded-md py-2 pl-11 pr-3 text-sm transition-colors duration-150 ${
                    activeSlug.includes(r.to)
                      ? "text-white"
                      : "text-white/65 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {r.label}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    const item = (
      <div
        key={link.id}
        className={itemClass(active)}
        style={itemStyle(active)}
        onClick={() => go(link.link)}
      >
        {body}
      </div>
    );

    return showLabels ? (
      item
    ) : (
      <Tooltip title={link.label} key={link.id} placement="right">
        {item}
      </Tooltip>
    );
  };

  const supportItem = (
    <div
      className={itemClass(false)}
      onClick={() => {
        openDialog("supportModal", {
          dialogHeader: "Need to report an issue? Have a question?",
        });
        if (onNavigate) onNavigate();
      }}
    >
      <LuCircleHelp size={18} className="shrink-0" />
      {showLabels && <span className="text-sm">Support</span>}
    </div>
  );

  return (
    <div
      className="flex h-full flex-col justify-between overflow-y-auto overflow-x-hidden"
      style={{ backgroundColor: SIDEBAR_BG }}
    >
      <div className="flex flex-col">
        {/* Brand: a rounded white card inset in the dark rail, not a full-bleed
            bar. Collapsed swaps the wordmark for the square mark, which stays
            legible at rail width. */}
        <div className={`${showLabels ? "px-3" : "px-2"} pt-3 pb-2`}>
          <Link
            to={ROUTES.defaultRoute}
            onClick={() => onNavigate && onNavigate()}
            className={`flex items-center justify-center rounded-md bg-white no-underline ${
              showLabels ? "h-[43px] px-3" : "h-10 px-1"
            }`}
          >
            <Image
              preview={false}
              src={showLabels ? Logo : LogoMark}
              alt="EngagedAI"
              className={`object-contain ${showLabels ? "h-[27px]" : "h-7"} w-auto`}
            />
          </Link>
        </div>

        <div className={`flex flex-col gap-1 py-2 ${showLabels ? "px-3" : "px-2"}`}>
          {NAVIGATION_OPTIONS.map(renderItem)}
        </div>
      </div>

      <div className={`pb-2 ${showLabels ? "px-3" : "px-2"}`}>
        {showLabels ? (
          supportItem
        ) : (
          <Tooltip placement="right" title="Support">
            {supportItem}
          </Tooltip>
        )}

        {!isDrawer && (
          <Tooltip placement="right" title={collapsed ? "Expand" : "Collapse"}>
            <div
              onClick={() => handleNavigationWidth()}
              className="mt-1 flex h-10 cursor-pointer items-center justify-center rounded-md text-white/65 transition-colors duration-150 hover:bg-white/10 hover:text-white"
            >
              {collapsed ? <LuChevronRight size={18} /> : <LuChevronLeft size={18} />}
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default NavigationContainer;
